import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";

ChartJS.register(ArcElement, Tooltip, Legend);

const AxixSipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            "question": "What is the Axis Mutual Fund SIP Calculator?",
            "answer": "It’s an online tool that estimates the future value of your SIP investments based on amount, return rate, and duration."
          },
          {
            "question": "Is it free to use?",
            "answer": "Yes, the Axis SIP Calculator is completely free for everyone."
          },
          {
            "question": "What inputs are required?",
            "answer": "Monthly SIP amount, expected annual return (%), and investment duration (in years/months)."
          },
          {
            "question": "Can I plan for a financial goal using the calculator?",
            "answer": "Yes, the goal-based mode allows you to reverse-calculate the SIP amount needed to reach a desired corpus."
          },
          {
            "question": "How accurate are the results?",
            "answer": "They are close approximations based on assumed returns. Actual market returns may vary."
          },
          {
            "question": "What formula does it use?",
            "answer": "Compound interest-based formula:   M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)"
          },
          {
            "question": "What is the minimum SIP amount for Axis Mutual Fund?",
            "answer": "You can start SIPs with as little as ₹500/month."
          },
          {
            "question": "Can I modify my SIP later?",
            "answer": "Yes, you can increase, decrease, or stop your SIP based on your financial needs."
          },
          {
            "question": "What is the maximum tenure allowed in the calculator?",
            "answer": "Typically up to 30-35 years for long-term planning."
          },
          {
            "question": "Can I use this for ELSS funds as well?",
            "answer": "Yes, though ELSS has a 3-year lock-in, the calculator works for all fund types."
          },
          {
            "question": "What if I miss a SIP payment?",
            "answer": "The calculator assumes regular payments. Missing SIPs will lower actual returns."
          },
          {
            "question": "How much does return rate influence SIP results?",
            "answer": "Significantly. A 1-2% change in return rate can lead to large differences in the maturity amount over long periods."
          },
          {
            "question": "Can it be used for non-Axis mutual funds?",
            "answer": "Yes, the math is universal. You can use the same calculator for any AMC’s SIPs."
          },
          {
            "question": "Are returns from Axis SIPs taxable?",
            "answer": "Yes. Long-term equity returns above ₹1 lakh are taxed at 10%, and short-term gains at 15%. Taxation rules vary for debt and hybrid funds."
          }
        ]
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
      <Helmet>
        <title>
        Axis Bank SIP Calculator – Smart Mutual Fund Investment Tool
        </title>
        <meta
          name="description"
          content="Use the Axis SIP Calculator to forecast your mutual fund investments. Make informed financial decisions with accurate SIP return estimates."
        />
    
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/axis-sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">Axix SIP Calculator</span>
          </li>
        </ol>
        
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          
          <h1 className="text-2xl font-bold mb-4">Axix SIP Returns Calculator</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
      <div className="w-full md:w-2/3 p-4 mx-auto">
      <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            AXIS Mutual Fund SIP Calculator
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Investing in mutual funds through Systematic Investment Plans (SIPs) has become a preferred choice for long-term wealth creation. Among India’s top AMCs, Axis Mutual Fund offers a comprehensive range of SIP-enabled schemes catering to diverse investment goals. To simplify investment planning, the Axis Mutual Fund SIP Calculator helps investors project the potential value of their investments based on consistent contributions, expected returns, and tenure.
          </p></div>
          
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is SIP and Why Is It Popular?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Systematic Investment Plan (SIP) is a disciplined way of investing in mutual funds. Instead of investing a lump sum, investors contribute smaller, fixed amounts regularly—typically every month. This not only builds financial discipline but also reduces the risk of market timing. SIPs benefit from rupee cost averaging and the power of compounding, making them ideal for long-term financial goals such as retirement, children’s education, or wealth accumulation.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Axis Mutual Fund offers a range of SIP-friendly schemes across equity, debt, hybrid, and ELSS categories. Whether you’re aiming for tax savings or capital appreciation, Axis SIPs enable smart, structured investing. To complement this, the Axis SIP Calculator offers a clear estimation of potential returns, empowering you to plan and invest wisely.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Can the Axis SIP Calculator Help You?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Axis SIP Calculator is designed to help you visualize how your periodic investments can grow over time. With just a few inputs—monthly SIP amount, expected return, and duration—you can get instant projections of your total investment, estimated earnings, and final maturity amount.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Key Benefits:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Smart Financial Planning:</strong> Estimate how much you need to invest monthly to achieve a specific financial target.</li>
              <li><strong>Quick & Accurate Projections:</strong> Avoid complex manual calculations with fast, reliable results.</li>
              <li><strong>Goal-Oriented Tracking:</strong> Tailor SIPs for specific goals like a home, car, or higher education.</li>
              <li><strong>Growth Insights:</strong> See both graphical and numeric breakdowns of principal vs. returns.</li>
              <li><strong>Scenario Analysis:</strong> Adjust expected return rates and duration to plan for market uncertainties.</li>
              <li><strong>Informed Investment Decisions:</strong> Understand the long-term impact of increasing SIP amounts or tenure.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Axis Mutual Fund SIP Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator uses a compound interest-based formula to estimate your investment’s maturity value:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Formula:</strong><br />
              M = P × [(1 + i)<sup>n</sup> - 1] / i × (1 + i)
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>M = Maturity Amount</li>
              <li>P = SIP Investment per Month</li>
              <li>i = Monthly Interest Rate (Annual Rate ÷ 12)</li>
              <li>n = Total Number of Installments</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Suppose you invest ₹3,000/month for 5 years with an annual expected return of 12% (i.e., 1% monthly):<br />
              M = 3,000 × [(1 + 0.01)<sup>60</sup> - 1] / 0.01 × (1 + 0.01)<br />
              M ≈ ₹2,43,400<br />
              So, a total investment of ₹1,80,000 would grow to approximately ₹2.43 lakhs in 5 years.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use the Axis SIP Calculator Online
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the Axis Mutual Fund SIP Calculator is simple and intuitive:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Visit the Calculator – Available on platforms like TheFinMaster or Axis Mutual Fund’s official site.</li>
              <li>Enter Monthly Investment – E.g., ₹5,000.</li>
              <li>Choose Expected Annual Return – Say, 11%.</li>
              <li>Select Tenure – For instance, 10 years.</li>
              <li>Get Instant Results:<br />
                <ul className="list-disc pl-6 mt-2">
                  <li>Total investment: ₹6,00,000</li>
                  <li>Estimated returns: ₹4,31,247</li>
                  <li>Maturity value: ₹10,31,247</li>
                </ul>
              </li>
            </ol>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You can also use the Goal-Based Mode to input your desired corpus and get the monthly SIP amount needed to achieve it.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Choose TheFinMaster SIP Calculator for Axis Mutual Fund?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SIP calculator on TheFinMaster is optimized for ease and advanced planning:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>User-Friendly Interface:</strong> Clean layout with slider inputs and real-time visuals.</li>
              <li><strong>Goal Planning Mode:</strong> Choose between wealth creation and goal-based targets.</li>
              <li><strong>Multiple Scenarios:</strong> Compare different tenures and return rates side by side.</li>
              <li><strong>No Sign-Up Needed:</strong> Use it instantly, no login required.</li>
              <li><strong>Learning Resources:</strong> Linked with SIP and mutual fund guides for deeper understanding.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors That Influence SIP Returns
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Monthly SIP Amount: Higher investments result in greater returns over time.
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Duration:</strong> Long-term SIPs benefit more from compounding, especially beyond 10+ years.</li>
              <li><strong>Expected Rate of Return:</strong> This varies depending on fund type and market performance.</li>
              <li><strong>Market Volatility:</strong> While SIPs average out volatility, short-term fluctuations can still impact returns.</li>
              <li><strong>Fund Selection:</strong> Axis offers large-cap, mid-cap, hybrid, and ELSS funds. Your choice impacts risk and returns.</li>
              <li><strong>Taxation:</strong> ELSS investments qualify for tax benefits, while long-term capital gains (LTCG) on equity funds above ₹1 lakh are taxed at 10%.</li>
            </ul>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
             {/* FAQ section */}
    <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default AxixSipCalculator;
